/**
 * Retrieves an HTMLElement from the DOM based on the provided selector. You can use
 * this if you are certain that the element exists as it will throw an error if the element
 * does not exist.
 *
 * This is an API to make working with the DOM a bit easier by removing null checks in
 * implementation code.
 *
 * @example
 * ```ts
 * const myElement = getDefinitiveNode<HTMLDivElement>('#my-element-id');
 * ```
 * @param selector - The CSS selector string to locate the element.
 * @param root - The root element to start the search from. Defaults to document.body.
 * @returns The found HTMLElement of type R.
 *
 * @throws Error if the element is not found.
 */
export function getDefinitiveNode<R extends HTMLElement>(selector: string, root = document.body): R {
  const element = root.querySelector<R>(selector);
  if (!element) {
    throw new Error(`Element with selector "${selector}" not found.`);
  }
  return element;
}
